'use client'

import React from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Section } from '@/components/ui/section'
import { Container } from '@/components/ui/container'
import type { CustomerItem } from '@/lib/api/site'
import { cn } from '@/lib/utils'

export type EventItemDisplay = {
  title?: string
  category?: string
  date?: string
  image?: string
  href?: string
}

function EventCard({
  item,
  className,
}: {
  item: EventItemDisplay
  className?: string
}) {
  const [imageLoaded, setImageLoaded] = React.useState(true)

  const content = (
    <div
      className={cn(
        'group relative h-full w-full overflow-hidden bg-charcoal-900 cursor-default rounded-none',
        className,
      )}
    >
      {/* Background Image */}
      {item.image && imageLoaded ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={item.image}
          alt={item.title || 'Dcorp Event'}
          loading="lazy"
          onError={() => setImageLoaded(false)}
          className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-1000 group-hover:scale-105 filter grayscale-[30%] group-hover:grayscale-0"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#1c2e4a] via-[#112035] to-[#0a1424]" />
      )}

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/95 via-charcoal-900/40 to-transparent opacity-90 transition-opacity duration-700" />

      {/* Subtle decorative inner border */}
      <div className="absolute inset-4 border border-white/10 group-hover:border-white/30 transition-colors duration-700 z-10 pointer-events-none" />

      {/* Bottom Content */}
      <div className="absolute bottom-0 left-0 w-full flex flex-col justify-end p-8 md:p-10 transform transition-transform duration-700 ease-out group-hover:-translate-y-2 z-20">
        {item.category ? (
          <div className="flex items-center gap-3 mb-3">
            <div className="w-6 h-[1px] bg-brand-red shrink-0" />
            <span className="text-brand-red text-[10px] font-mono font-bold uppercase tracking-[0.2em]">
              {item.category.replace(/^[—\-]\s*/, '')}
            </span>
          </div>
        ) : null}

        {item.title ? (
          <h3 className="text-lg lg:text-xl font-bold text-white leading-snug tracking-tight min-h-[3rem] lg:min-h-[3.5rem] line-clamp-2">
            {item.title}
          </h3>
        ) : null}
      </div>
    </div>
  )

  if (item.href) {
    return (
      <Link href={item.href} className="block h-full w-full">
        {content}
      </Link>
    )
  }

  return content
}

function renderEventsGrid(items: EventItemDisplay[]) {
  const layoutMap: Record<number, { colSpan: string; height: string }> = {
    0: { colSpan: 'md:col-span-8', height: 'h-[360px] lg:h-[500px]' },
    1: { colSpan: 'md:col-span-4', height: 'h-[360px] lg:h-[500px]' },
    2: { colSpan: 'md:col-span-5', height: 'h-[360px] lg:h-[400px]' },
    3: { colSpan: 'md:col-span-7', height: 'h-[360px] lg:h-[400px]' },
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6 w-full">
      {items.map((item, idx) => {
        const layout = layoutMap[idx % 4] || { colSpan: 'md:col-span-6', height: 'h-[360px] lg:h-[400px]' }
        return (
          <div
            key={`${item.title}-${idx}`}
            className={cn(
              layout.colSpan,
              layout.height,
              'w-full',
            )}
          >
            <EventCard item={item} />
          </div>
        )
      })}
    </div>
  )
}

export function HomeEventsSection({ events }: { events?: CustomerItem[] }) {
  const t = useTranslations('homePage')

  const items = React.useMemo(() => {
    if (!events || events.length === 0) return []

    // Sort by sort_order / sortOrder ascending
    const sorted = [...events].sort((a, b) => {
      const orderA = a.sort_order ?? a.sortOrder ?? 999
      const orderB = b.sort_order ?? b.sortOrder ?? 999
      return orderA - orderB
    })

    return sorted.map((ev): EventItemDisplay => ({
      title: ev.name,
      category: ev.tagline || ev.holdingGroup || ev.holding_group,
      date: ev.storeCount || ev.store_count,
      image: ev.logoUrl || ev.logo_url,
      href: ev.website,
    }))
  }, [events])

  if (!items || items.length === 0) {
    return null
  }

  return (
    <Section
      tone="gray50"
      spacing="py32"
      border="bGray200"
      className="bg-gray-50 py-32 border-b border-gray-200"
    >
      <Container size="site">
        <div className="mb-16">
          <h2 className="text-[30px] font-bold text-charcoal-900 tracking-tight mb-4 uppercase">
            {t('events.title', { defaultMessage: 'SỰ KIỆN & HOẠT ĐỘNG' })}
          </h2>
          <p className="text-charcoal-600 text-[16px] font-light md:whitespace-nowrap overflow-hidden text-ellipsis">
            {t('events.desc', {
              defaultMessage:
                'Cập nhật những sự kiện công nghệ, hội chợ, triển lãm ngành F&B và tin tức mới nhất về Dcorp.',
            })}
          </p>
        </div>

        {renderEventsGrid(items)}
      </Container>
    </Section>
  )
}
